import {useState} from 'react';
import {useSetAtom, useAtomValue} from 'jotai';
import {useNavigate, useLocation, Navigate, Link} from 'react-router';
import {Box, Paper, TextField, Button, Typography, Alert, Stack} from '@mui/material';
import {signInAtom, authStatusAtom, isOnlineAtom} from '@/state/authAtoms';
import {useDevice} from '@/hooks/useDevice.ts';
import {routerLinks} from '@/Constants.ts';

export function LoginPage() {
   const signIn = useSetAtom(signInAtom);
   const status = useAtomValue(authStatusAtom);
   const isOnline = useAtomValue(isOnlineAtom);
   const navigate = useNavigate();
   const location = useLocation();

   const [username, setUsername] = useState('');
   const [password, setPassword] = useState('');
   const [error, setError] = useState<string | null>(null);
   const [submitting, setSubmitting] = useState(false);
   const device = useDevice();

   if (status === 'authenticated' || status === 'offline-cached') {
      const from = (location.state as {from?: Location})?.from?.pathname ?? '/';
      return <Navigate to={from} replace />;
   }

   const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setSubmitting(true);
      try {
         await signIn({username, password});
         const from = (location.state as {from?: Location})?.from?.pathname ?? '/';
         navigate(from, {replace: true});
      } catch (err) {
         setError(err instanceof Error ? err.message : 'Sign in failed.');
      } finally {
         setSubmitting(false);
      }
   };

   return (
      <Box
         sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            bgcolor: 'background.default',
         }}
      >
         <Paper
            sx={{
               p: 4,
               width: device?.isPhoneOrTablet ? '100%' : 360,
               height: device?.isPhoneOrTablet ? '100%' : undefined,
               display: 'flex',
               justifyContent: 'center',
               flexDirection: 'column',
               alignItems: 'center',
            }}
            elevation={3}
         >
            <img src={'/images/logo-large.png'} width={160} height={160} />
            <Typography variant='h5' sx={{mt: 1}}>
               WARRIOR Production
            </Typography>
            <Typography variant='body2' sx={{mb: 3}}>
               Sign in to continue
            </Typography>

            {!isOnline && (
               <Alert severity='warning' sx={{mb: 2}}>
                  You're offline. Signing in requires a connection the first time; after that a cached session lets you
                  work offline for a limited period.
               </Alert>
            )}

            {error && (
               <Alert severity='error' sx={{mb: 2}}>
                  {error}
               </Alert>
            )}

            <Box component='form' onSubmit={handleSubmit} sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
               <TextField
                  label='Username'
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoFocus
                  required
               />
               <TextField
                  label='Password'
                  type='password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
               />
               {isOnline && (
                  <Stack direction={'row'} sx={{justifyContent: 'flex-end'}}>
                     <Button variant='text' disabled={submitting} component={Link} to={routerLinks.forgotPassword}>
                        Forgot Password
                     </Button>
                  </Stack>
               )}
               <Button type='submit' variant='contained' disabled={submitting || !isOnline}>
                  {submitting ? 'Signing in…' : 'Sign in'}
               </Button>
            </Box>
         </Paper>
      </Box>
   );
}
